import Avatar from 'components/avatarComponent';
import React, { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { ReactComponent as Arrow } from 'assets/icons/upArrow.svg';
import { useGetSignUrlFromStorageForRequesterLazyQuery } from 'graphql/generated/hasura';
import { useAuth } from '../../../auth/context/AuthContext';

interface AccountCardProps {
  profilePicture?: string | null;
  name: string;
  id: string;
  isChild?: boolean;
  isDropdown?: boolean;
  isFocused?: boolean;
  bgClasses?: string;
  borderClasses?: string;
  widthClasses?: string;
  handleClickAccount?: () => void;
  showIndicator?: boolean;
}

export const AccountCard: React.FC<AccountCardProps> = ({
  profilePicture,
  name,
  id,
  isChild = false,
  isDropdown = false,
  isFocused = false,
  showIndicator = false,
  widthClasses = 'w-full',
  bgClasses = 'bg-base-content',
  borderClasses = 'border border-graph-border-color',
  handleClickAccount,
}) => {
  const { selectedAccount, user, avatarMap } = useAuth();
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    avatarMap?.get(id) ?? null,
  );

  const [getFileUrlFromStorageForRequester] =
    useGetSignUrlFromStorageForRequesterLazyQuery();

  const getAvatar = useCallback(async () => {
    let profileImage = null;
    if (!profilePicture || !id) return profileImage;
    if (avatarMap?.has(id)) {
      return avatarMap?.get(id);
    }
    profileImage = await getFileUrlFromStorageForRequester({
      variables: {
        fileId: profilePicture,
        requestedUserId: id,
      },
    });
    return profileImage?.data?.GetSignUrlFromStorageForRequester?.url;
  }, [avatarMap, getFileUrlFromStorageForRequester, id, profilePicture]);

  useEffect(() => {
    getAvatar()
      .then((image) => {
        setAvatarUrl(image ?? '');
      })
      .catch((error) => console.error(error));
  }, [getAvatar]);

  const isActive =
    selectedAccount === id ||
    (user?.uuid === selectedAccount && !selectedAccount);

  return (
    <div
      onClick={handleClickAccount}
      className={twMerge(
        `${widthClasses} ${bgClasses} ${borderClasses} rounded-10 group flex justify-between items-center p-2.5`,
        isActive && !isDropdown && ' border-azure-blue ',
        !isDropdown && 'hover:bg-hover-blue',
      )}
    >
      <div className="flex w-full items-center justify-center px-0">
        <div className="relative">
          {showIndicator && (
            <div className="absolute top-0 right-0 h-3 w-3 border border-white rounded-full bg-orange-body-dot" />
          )}
          <Avatar size="sm" imageUrl={avatarUrl} key={avatarUrl} />
        </div>

        <div className="flex flex-col ml-2.5 w-[80%] min-w-0">
          <div
            className={twMerge(
              'font-semibold text-lg text-neutral-800 w-full fill-clc-blue truncate',
              !isDropdown && 'group-hover:text-[#2B43DA]',
            )}
          >
            {name}
          </div>
          <div className="text-med-gray-3 text-sm">
            {!isChild ? 'My profile' : 'Child profile'}
          </div>
        </div>
      </div>

      {isDropdown && (
        <Arrow
          className={twMerge('w-3', isFocused ? 'rotate-0' : 'rotate-180')}
        />
      )}
    </div>
  );
};
